/**
 * Shared UI helpers for the Dealer Portal.
 * Keeps CSS/token strings & tiny presentation components DRY across pages.
 */
import React from "react";

/* Section title used at the top of each dealer page */
export const PageHeader = ({ title, subtitle, right, testId }) => (
  <div
    className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6"
    data-testid={testId || "dealer-page-header"}
  >
    <div>
      <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">
        {title}
      </h1>
      {subtitle && (
        <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
      )}
    </div>
    {right && <div className="flex flex-wrap items-center gap-2">{right}</div>}
  </div>
);

/* Section container that reserves consistent horizontal padding */
export const PageContainer = ({ children, className = "" }) => (
  <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 ${className}`}>
    {children}
  </div>
);

/* Small status pill */
export const StatusPill = ({ tone = "slate", children, className = "" }) => {
  const tones = {
    slate: "bg-slate-100 text-slate-700 border-slate-200",
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    gold: "bg-[#FCF3D9] text-[#8B6914] border-[#F1E1A8]",
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 border rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${tones[tone] || tones.slate} ${className}`}
    >
      {children}
    </span>
  );
};

/* Empty state */
export const EmptyState = ({ icon: Icon, title, description, action, testId }) => (
  <div
    className="border border-dashed border-slate-200 rounded-2xl px-6 py-14 text-center bg-white"
    data-testid={testId || "empty-state"}
  >
    {Icon && (
      <div className="mx-auto w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center text-slate-400 mb-4">
        <Icon size={20} />
      </div>
    )}
    <p className="font-display text-lg font-semibold text-slate-800">{title}</p>
    {description && (
      <p className="mt-1 text-sm text-slate-500 max-w-md mx-auto">{description}</p>
    )}
    {action && <div className="mt-5 flex justify-center">{action}</div>}
  </div>
);

/* Loading skeleton block */
export const Skeleton = ({ className = "" }) => (
  <div className={`animate-pulse bg-slate-100 rounded-md ${className}`} />
);

/* Lead status meta */
export const LEAD_STATUS = {
  new: { label: "New", tone: "slate", color: "#64748b" },
  contacted: { label: "Contacted", tone: "blue", color: "#3b82f6" },
  appt_scheduled: { label: "Appt Scheduled", tone: "amber", color: "#d97706" },
  showed_up: { label: "Showed Up", tone: "emerald", color: "#10b981" },
  no_show: { label: "No Show", tone: "rose", color: "#f43f5e" },
  sold: { label: "Sold", tone: "emerald", color: "#059669" },
  lost: { label: "Lost", tone: "slate", color: "#94a3b8" },
};

export const APPT_STATUS = {
  scheduled: { label: "Scheduled", tone: "amber" },
  showed_up: { label: "Showed Up", tone: "emerald" },
  no_show: { label: "No Show", tone: "rose" },
  cancelled: { label: "Cancelled", tone: "slate" },
  rescheduled: { label: "Rescheduled", tone: "blue" },
};

export const SYNC_SOURCE_LABEL = {
  vinsolutions: "VinSolutions",
  dealercenter: "DealerCenter",
  vauto: "vAuto",
  manual: "Manual",
  manual_csv: "CSV upload",
};

/* Human-friendly relative time */
export const relativeTime = (iso) => {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
};

export const formatDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

export const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};
